/**
 * BYOB Cart Sync - Polling-based approach
 * Ensures engraving products stay linked with main products
 * Works with GoKwik and any cart system
 */

(function() {
  'use strict';

  const ENGRAVING_VARIANT_ID = 48569060327647;
  let isProcessing = false;
  let lastCartState = '';
  let isEngravingEnabled = false;

  /**
   * Check if engraving is enabled
   */
  function checkEngravingEnabled() {
    const wrapper = document.querySelector('.byob-wrapper');
    if (wrapper) {
      isEngravingEnabled = wrapper.dataset.enableEngraving === 'true';
      console.log('🔧 Engraving enabled:', isEngravingEnabled);
    }
  }

  /**
   * Sync cart - remove orphaned products ONLY when user manually removes items
   */
  async function syncCart(triggeredByUser = false) {
    // If engraving is disabled, don't run sync logic
    if (!isEngravingEnabled) {
      console.log('⏸️ Engraving disabled, cart sync skipped');
      return;
    }

    // Only run if triggered by user action (manual removal)
    if (!triggeredByUser) {
      console.log('⏸️ Auto-sync disabled, only manual removal triggers sync');
      return;
    }

    if (isProcessing) {
      console.log('Already processing, skipping...');
      return;
    }

    try {
      isProcessing = true;
      
      console.log('🔄 User removed item, checking for orphaned products...');
      
      // Get current cart
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      const itemsToRemove = [];
      const engravingItems = [];
      const mainProducts = [];

      // Categorize items
      cart.items.forEach(item => {
        if (item.variant_id === ENGRAVING_VARIANT_ID) {
          engravingItems.push(item);
        } else if (item.properties && item.properties['Engrave Name']) {
          mainProducts.push(item);
        }
      });

      console.log(`Found ${engravingItems.length} engraving items and ${mainProducts.length} main products with engraving`);

      // Check for orphaned engraving products
      engravingItems.forEach(engraveItem => {
        const linkedProductTitle = engraveItem.properties['_for_product'];
        const hasMainProduct = mainProducts.some(p => p.product_title === linkedProductTitle);
        
        if (!hasMainProduct) {
          console.log('⚠️ Orphaned engraving found for:', linkedProductTitle);
          itemsToRemove.push(engraveItem.key);
        }
      });

      // Check for orphaned main products - ONLY if they have engraving name but no engraving product
      mainProducts.forEach(mainItem => {
        // Only check if the product actually has an engraving name
        if (mainItem.properties['Engrave Name'] && mainItem.properties['Engrave Name'].trim()) {
          const hasEngraving = engravingItems.some(e => 
            e.properties['_for_product'] === mainItem.product_title
          );
          
          if (!hasEngraving) {
            console.log('⚠️ Orphaned main product found:', mainItem.product_title);
            itemsToRemove.push(mainItem.key);
          }
        }
      });

      // Remove orphaned items
      if (itemsToRemove.length > 0) {
        console.log('🗑️ Removing', itemsToRemove.length, 'orphaned items');
        
        for (const itemKey of itemsToRemove) {
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              id: itemKey,
              quantity: 0
            })
          });
        }

        console.log('✅ Orphaned items removed');
        
        // Trigger cart refresh
        document.dispatchEvent(new CustomEvent('cart:updated'));
        
        // Force reload if on cart page
        if (window.location.pathname.includes('/cart')) {
          setTimeout(() => {
            window.location.reload();
          }, 300);
        }
      }

    } catch (error) {
      console.error('❌ Error syncing cart:', error);
    } finally {
      isProcessing = false;
    }
  }

  /**
   * Monitor cart for changes and sync when items are removed
   */
  function setupCartMonitoring() {
    // Check if engraving is enabled first
    checkEngravingEnabled();
    
    if (!isEngravingEnabled) {
      return;
    }
        
    let previousCartItems = [];
    
    // Check cart periodically but only sync when items are REMOVED (not added)
    setInterval(async () => {
      try {
        const response = await fetch('/cart.js');
        const cart = await response.json();
        const currentCartItems = cart.items.map(i => i.key);
        
        // First time, just store the cart state
        if (previousCartItems.length === 0) {
          previousCartItems = currentCartItems;
          return;
        }
        
        // Check if any items were REMOVED (not added)
        const itemsRemoved = previousCartItems.filter(key => !currentCartItems.includes(key));
        
        if (itemsRemoved.length > 0) {
          console.log('🗑️ User removed items:', itemsRemoved);
          // User removed something, trigger sync
          await syncCart(true);
        }
        
        // Update previous state
        previousCartItems = currentCartItems;
        
      } catch (error) {
        console.error('Error monitoring cart:', error);
      }
    }, 1000); // Check every 1 second
  }

  // Setup monitoring when page loads
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setupCartMonitoring);
  } else {
    setupCartMonitoring();
  }

})();
